// src/components/Filters.js
import React, { useMemo, useState } from 'react';
import Select from 'react-select';
import ToolsModal from './ToolsModal';
import { 
  hasSchoolProblems, 
  getDistrictProblemSchoolsCount,
  isDateOutdatedTmRk,
  isDateOutdatedcp,
  extractSchoolNumber,
  THRESHOLDS
} from '../utils/calculations';

const Filters = ({ filters, updateFilters, allData, onReload, loading }) => {
  const [showToolsModal, setShowToolsModal] = useState(false);

  // Функция для подсчета количества ОУ с проблемами в районе
  const getDistrictProblemSchoolsCountWrapper = (districtSchools) => {
    return getDistrictProblemSchoolsCount(districtSchools, allData, filters);
  };

  // Получаем список районов с количеством проблемных ОУ (сортировка по алфавиту)
  const districtOptions = useMemo(() => {
    const districtsMap = new Map();
    
    allData.dates.forEach(school => {
      const district = school.РайонНаименование;
      if (!district) return;
      
      if (!districtsMap.has(district)) {
        districtsMap.set(district, {
          name: district,
          schools: []
        });
      }
      districtsMap.get(district).schools.push(school);
    });
    
    const districts = Array.from(districtsMap.values()).map(district => {
      const problemSchoolsCount = getDistrictProblemSchoolsCountWrapper(district.schools);
      return {
        value: district.name,
        label: district.name,
        problemSchoolsCount: problemSchoolsCount,
        schoolsCount: district.schools.length
      };
    });
    
    const sorted = districts.sort((a, b) => a.label.localeCompare(b.label, 'ru'));
    
    return [
      { value: '', label: 'Все районы', problemSchoolsCount: 0, schoolsCount: 0 },
      ...sorted
    ];
  }, [allData, filters]);

  // Получаем список всех школ города
  const allSchoolsOptions = useMemo(() => {
    const allSchools = allData.dates.map(school => ({
      value: school.Код.toString(),
      label: school.Наименование,
      hasProblems: hasSchoolProblems(school, allData, filters),
      schoolNumber: extractSchoolNumber(school.Наименование) || 9999
    }));
    
    const sorted = allSchools.sort((a, b) => a.schoolNumber - b.schoolNumber);
    
    return [
      { value: '', label: 'Все учреждения', hasProblems: false },
      ...sorted
    ];
  }, [allData, filters]);

  // Получаем список школ для выбранного района
  const districtSchoolsOptions = useMemo(() => {
    if (!filters.district) return [];
    
    const districtSchools = allData.dates.filter(item => 
      item.РайонНаименование === filters.district
    );
    
    const schools = districtSchools.map(school => ({
      value: school.Код.toString(),
      label: school.Наименование,
      hasProblems: hasSchoolProblems(school, allData, filters),
      schoolNumber: extractSchoolNumber(school.Наименование) || 9999
    }));
    
    const sorted = schools.sort((a, b) => a.schoolNumber - b.schoolNumber);
    
    return [
      { value: '', label: 'Все учреждения', hasProblems: false },
      ...sorted
    ];
  }, [allData, filters]);

  const schoolOptions = useMemo(() => {
    if (filters.district) {
      return districtSchoolsOptions;
    }
    return allSchoolsOptions;
  }, [filters.district, districtSchoolsOptions, allSchoolsOptions]);

  // Формат опции для района - добавляем значок ⚠️ для проблемных районов
  const formatDistrictOption = (option) => {
    if (!option.value) return option.label;
    if (option.problemSchoolsCount === 0) return option.label;
    // Добавляем восклицательный знак и количество проблемных ОУ в скобках
    return `${option.label} ⚠️ (${option.problemSchoolsCount})`;
  };

  // Формат опции для школы - добавляем значок ⚠️ для проблемных школ
  const formatSchoolOption = (option) => {
    if (!option.value) return option.label;
    if (!option.hasProblems) return option.label;
    // Добавляем восклицательный знак
    return `${option.label} ⚠️`;
  };

  const handleDistrictChange = (option) => {
    updateFilters({ district: option?.value || '', school: '' });
  };

  const handleSchoolChange = (option) => {
    updateFilters({ school: option?.value || '' });
  };

  const handleMonthChange = (month) => {
    updateFilters({ month: parseInt(month) });
  };

  const handleYearChange = (year) => {
    updateFilters({ year: parseInt(year) });
  };

  const openToolsModal = () => {
    setShowToolsModal(true);
  };

  const closeToolsModal = () => {
    setShowToolsModal(false);
  };

  const customStyles = {
    control: (base, state) => ({
      ...base,
      minWidth: 200,
      borderRadius: 4,
      borderColor: state.isFocused ? '#1a3a5c' : '#e2e8f0',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(26, 58, 92, 0.1)' : 'none',
      '&:hover': {
        borderColor: '#1a3a5c'
      }
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused ? '#edf2f7' : 'white',
      color: '#2d3748',
      cursor: 'pointer',
      padding: '8px 12px',
      fontSize: '0.75rem'
    }),
    menu: (base) => ({
      ...base,
      zIndex: 999,
      borderRadius: 4,
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
    }),
    singleValue: (base) => ({
      ...base,
      color: '#2d3748',
      fontSize: '0.75rem'
    }),
    placeholder: (base) => ({
      ...base,
      color: '#a0aec0',
      fontSize: '0.75rem'
    })
  };

  return (
    <>
      <div className="filters">
        <div className="filter-group">
          <label htmlFor="district-select">Район</label>
          <Select
            id="district-select"
            options={districtOptions}
            value={districtOptions.find(opt => opt.value === filters.district) || districtOptions[0]}
            onChange={handleDistrictChange}
            formatOptionLabel={formatDistrictOption}
            styles={customStyles}
            placeholder="Выберите район"
            isClearable
          />
        </div>

        <div className="filter-group">
          <label htmlFor="school-select">ОУ</label>
          <Select
            id="school-select"
            options={schoolOptions}
            value={schoolOptions.find(opt => opt.value === filters.school) || null}
            onChange={handleSchoolChange}
            formatOptionLabel={formatSchoolOption}
            styles={customStyles}
            placeholder="Выберите учреждение"
            isClearable
            isDisabled={!filters.district}
          />
        </div>

        <div className="filter-group">
          <label htmlFor="month-select">Месяц</label>
          <select
            id="month-select"
            value={filters.month}
            onChange={(e) => handleMonthChange(e.target.value)}
            className="native-select"
          >
            <option value="1">Январь</option>
            <option value="2">Февраль</option>
            <option value="3">Март</option>
            <option value="4">Апрель</option>
            <option value="5">Май</option>
            <option value="6">Июнь</option>
            <option value="7">Июль</option>
            <option value="8">Август</option>
            <option value="9">Сентябрь</option>
            <option value="10">Октябрь</option>
            <option value="11">Ноябрь</option>
            <option value="12">Декабрь</option>
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="year-select">Год</label>
          <select
            id="year-select"
            value={filters.year}
            onChange={(e) => handleYearChange(e.target.value)}
            className="native-select"
          >
            <option value="2023">2023</option>
            <option value="2024">2024</option>
            <option value="2025">2025</option>
            <option value="2026">2026</option>
          </select>
        </div>

        <div className="filter-group">
          <label>&nbsp;</label>
          <button 
            id="update-btn" 
            onClick={onReload}
            disabled={loading}
          >
            {loading ? 'Загрузка...' : 'Обновить'}
          </button>
        </div>

        <div className="filter-group tools-group">
          <button 
            className="tools-btn"
            onClick={openToolsModal}
            title="Генератор меню"
          >
            🍽️ГЕНЕРАТОР МЕНЮ
          </button>
        </div>
      </div>

      <ToolsModal 
        isOpen={showToolsModal} 
        onClose={closeToolsModal}
      />
    </>
  );
};

export default Filters;